import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ProviderTypes } from '@guryou/core';
import {
  Autocomplete,
  Block,
  CheckIcon,
  ClockIcon,
  ContainedButton,
  DefaultText,
  FullScreenSpinner,
  IconTextButtonOutlined,
  LocationIcon,
  MediumText,
  MyBlockIcon,
  openModal,
  OutlinedButton,
  RadioGroupBox,
  SmallGrayText,
  SmallText,
  TextBox,
} from '@guryou/html-components';
import { createProviderOnboard, stateIsLoading, useAllProviderCategories, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import SelectStaff from 'onboard/components/SelectStaff';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';

import CheckboxInfoContainer from '../containers/CheckboxInfoContainer';
import MainContainer from '../containers/MainContainer';

const ERROR_COLOR = '#E53C4C';

const getProviderTextByType = (type, t) => {
  if (type === ProviderTypes.centers) {
    return {
      title: t('ONBOARD.centerTitle'),
      subTitle: t('ONBOARD.centerSubtitle'),
      location: t('ONBOARD.centerLocation'),
      workingHours: t('ONBOARD.centerWorkingHours'),
      staff: t('ONBOARD.centerStaff'),
    };
  }
  return {
    title: t('ONBOARD.profTitle'),
    subTitle: t('ONBOARD.profSubtitle'),
    location: t('ONBOARD.profLocation'),
    workingHours: t('ONBOARD.profWorkingHours'),
    staff: '',
  };
};

const ProviderGeneralInfo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [radio, setRadio] = useState('true');
  const [categoryError, setCategoryError] = useState(null);

  const { state, errors, setErrors, schedules, location, nextActiveStep, setCreatedProviderId, resetState, setActiveStep } = useOnboardContext();

  const allProviderCategories = useAllProviderCategories();
  const providerCreate = useSelector(state => state.providers.createOnboard);
  const auth = useSelector(state => state.auth);

  useEffectOnSuccess(
    state => state.providers.createOnboard,
    data => {
      setCreatedProviderId(data.id);
      nextActiveStep();
    }
  );

  useEffectOnError(
    state => state.providers.createOnboard,
    () => {
      setActiveStep(-1);
    }
  );

  const handleChangeRadio = e => {
    setRadio(e.target.value);
  };

  const handleSubmit = data => {
    if (!isLocationSet || !schedules) {
      setErrors({
        providerLocation: !isLocationSet,
        providerSchedules: !schedules,
      });
      return;
    }
    const email = auth.data?.email;
    const type = state.data?.providerType;
    const categoryIds = []
      .concat(data?.categories || [])
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    const rawStaff = data?.staffNames;
    const staff = Array.isArray(rawStaff)
      ? rawStaff.filter(name => typeof name === 'string' && name.trim() !== '')
      : typeof rawStaff === 'string' && rawStaff.trim() !== ''
      ? [rawStaff.trim()]
      : null;

    if (!categoryIds || categoryIds.length < 1) {
      setCategoryError(true);
      return;
    }

    dispatch(
      createProviderOnboard({
        name: data.name.trim(),
        categoryIds: categoryIds,
        staffNames: staff,
        type,
        email,
        schedules,
        ...location,
      })
    );
  };

  const providerType = state.data.providerType;
  const providerText = getProviderTextByType(providerType, t);
  const filteredCategories = allProviderCategories?.data?.filter(x => x.type === providerType);
  const isLocationSet = Object.keys(location).length !== 0;

  if (stateIsLoading(providerCreate)) {
    return <FullScreenSpinner />;
  }

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit}>
        <TitleContainer title={providerText.title} subTitle={providerText.subTitle} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={6}>
          <TextBox
            xs={12}
            fullWidth
            label={providerType === ProviderTypes.centers ? t('ONBOARD.centerName') : t('ONBOARD.professionalName')}
            margin="10px 0px 20px 0px"
            name="name"
            inputProps={{ maxLength: 100 }}
            required
          />

          <Autocomplete
            className="full-width-autocomplete"
            margin="none"
            required={true}
            isMultiSelect={true}
            name="categories"
            label={t('ONBOARD.category')}
            options={filteredCategories}
            value={null}
          />
          {categoryError && <SmallText color={ERROR_COLOR}>{t('ONBOARD.categoryRequired')}</SmallText>}
          <Block margin={0} marginTop={5} marginBottom={5}>
            <SmallGrayText marginBottom={2}>{providerText.location}</SmallGrayText>
            <IconTextButtonOutlined
              marginLeft={0}
              marginRight={2}
              icon={
                errors.providerLocation ? <MyBlockIcon htmlColor={ERROR_COLOR} /> : isLocationSet ? <CheckIcon htmlColor="green" /> : <LocationIcon />
              }
              text={t('ONBOARD.location')}
              color={errors.providerLocation ? ERROR_COLOR : isLocationSet ? 'green' : 'default'}
              onClick={() => dispatch(openModal('setLocation'))}
            />

            <IconTextButtonOutlined
              marginLeft={0}
              icon={errors.providerSchedules ? <MyBlockIcon htmlColor={ERROR_COLOR} /> : schedules ? <CheckIcon htmlColor="green" /> : <ClockIcon />}
              text={t('ONBOARD.schedule')}
              onClick={() => dispatch(openModal('setWorkingHours'))}
              color={errors.providerSchedules ? ERROR_COLOR : schedules ? 'green' : 'default'}
            />
          </Block>

          {providerType === ProviderTypes.centers && (
            <Block margin={0} marginTop={5} marginBottom={5}>
              <SmallGrayText>{providerText.staff}</SmallGrayText>
              <RadioGroupBox
                name="radio"
                labelPlacement="end"
                value={radio}
                onChange={handleChangeRadio}
                boxSize={12}
                options={[
                  { id: 'true', label: t('ONBOARD.noOnlyMe') },
                  { id: 'false', label: t('ONBOARD.yes') },
                ]}
              />
            </Block>
          )}
          {providerType === ProviderTypes.centers && radio === 'false' && <SelectStaff />}

          <OutlinedButton marginLeft={0} onClick={() => resetState()}>
            {t('ONBOARD.prev')}
          </OutlinedButton>

          <ContainedButton type="submit" marginLeft={0} color="secondary">
            {t('ONBOARD.next')}
          </ContainedButton>
        </Block>
      </FormContainer>
      <InfoContainer>
        <MediumText>{t('ONBOARD_TEXT.providerTitle')}</MediumText>
        <DefaultText fontSize="1.2rem">{t('ONBOARD_TEXT.providerSubtitle')}</DefaultText>
        <Block margin={0} marginTop={4}>
          <DefaultText fontSize="1.2rem" fontWeight="800">
            {t('ONBOARD_TEXT.providerCheckboxTitle')}
          </DefaultText>
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox1Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox2Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox3Text')} />
        </Block>
        <PlayGuideVideoEmbed videoId={guideVideos.providerGeneralInfo.videoId} modalTitle={guideVideos.providerGeneralInfo.title} />
      </InfoContainer>
    </MainContainer>
  );
};

export default ProviderGeneralInfo;
